import React from "react";
import { Container, Overlay } from "./styles";
import Menu from "../menu";
import Products from "../../screens/products";
import Clients from "../../screens/clients";
import Suppliers from "../../screens/suppliers";
import ReformaTributaria from "../../screens/reforma-tributaria";
import ValidacaoFiscal from "../../screens/validacao-fiscal";
import PLCom from "../../screens/plcom";
import Transactions from "../../screens/transactions";
import Files from "../../screens/files";
import NotificacoesFiscais from "../../screens/notificacoes-fiscais";
import Users from "../../screens/users";
import Admin from "../../screens/admin";
import Revisor from "../../screens/revisor";
import SpedProcessor from "../../screens/sped-processor";
import Downloads from "../../screens/downloads";
import Projects from "../../screens/projects";
import XmlScan from "../../screens/xml-scan";
import Creditos from "../../screens/creditos";
import Certificates from "../../screens/certificates";
import EmailMarketing from "../../screens/email-marketing";
import NfeDownloader from "../../screens/nfe-downloader";
import LeadScraper from "../../screens/lead-scraper";
import AutomecFornecedores from "../../screens/automec-fornecedores";
import ActivityLogs from "../../screens/activity-logs";
import api from "../../services/api";
import { isDemoMode } from "../../services/demoService";
import { usePersistedState } from "../../hooks/usePersistedState";
import { useEffect, useCallback, useRef } from "react";
import SubscriptionGate from "../SubscriptionGate";
import usePlanAccess from "../../hooks/usePlanAccess";
import { useAuth } from "../../contexts/AuthContext";
import { DASHBOARD_TO_MODULE } from "../../config/modules";
import { isAutomecProject } from "../../config/automec";

export default function Display({
  menuMinimized = false,
  electron = false,
  onMenuToggle,
  onOpenCertificateModal,
  onOpenXmlDownloadModal,
}) {
  // Tela ativa persistida - inicia em "files" se não houver valor salvo
  const [activeDashboard, setActiveDashboard] = usePersistedState(
    "vidal_active_screen",
    "files"
  );

  const changeDashboard = useCallback((dashboard) => {
    setActiveDashboard(dashboard);
  }, [setActiveDashboard]);

  const { canAccessDashboard } = usePlanAccess();
  const { hasPermission, currentProject } = useAuth();

  // Acesso definido tanto pelo plano quanto pelas permissões granulares do
  // usuário/projeto (respeitado também durante simulação de usuário — ver
  // AuthContext.hasPermission). Um dashboard só é acessível se as duas
  // checagens permitirem.
  const canAccessScreen = useCallback(
    (dashboardId) => {
      // Telas exclusivas de projetos específicos
      if (dashboardId === 'automec-fornecedores' && !isAutomecProject(currentProject)) return false;
      if (!canAccessDashboard(dashboardId)) return false;
      const moduleId = DASHBOARD_TO_MODULE[dashboardId];
      if (moduleId && !hasPermission(moduleId)) return false;
      return true;
    },
    [canAccessDashboard, hasPermission, currentProject]
  );

  // Guard: se o dashboard ativo não é liberado pelo plano/permissões, volta para o primeiro acessível
  useEffect(() => {
    if (!canAccessScreen(activeDashboard)) {
      const accessibleScreens = ['files', 'transactions', 'products', 'clients', 'suppliers', 'reforma-tributaria', 'validacao-fiscal', 'plcom', 'notificacoes-fiscais', 'sped-reviewer', 'downloads', 'xml-scan', 'creditos', 'certificates', 'email-marketing', 'users'];
      const firstAccessible = accessibleScreens.find(screen => canAccessScreen(screen));
      setActiveDashboard(firstAccessible || 'files');
    }
  }, [activeDashboard, canAccessScreen, setActiveDashboard]);

  // Registro de navegação (ravena-activity-logs). Mesma tela em menos de 30s
  // não é reenviada; modo demo nunca é registrado.
  const lastPageViewRef = useRef({ screen: null, at: 0 });
  useEffect(() => {
    if (isDemoMode() || !activeDashboard || !canAccessScreen(activeDashboard)) return;
    const now = Date.now();
    const last = lastPageViewRef.current;
    if (last.screen === activeDashboard && now - last.at < 30000) return;
    lastPageViewRef.current = { screen: activeDashboard, at: now };
    api.post("/activity-logs/page-view", { screen: activeDashboard }).catch(() => {});
  }, [activeDashboard, canAccessScreen]);

  // Listen for navigation events from StatusBar badges
  useEffect(() => {
    const handler = (e) => {
      if (e.detail) changeDashboard(e.detail);
    };
    window.addEventListener('navigate-dashboard', handler);
    return () => window.removeEventListener('navigate-dashboard', handler);
  }, [changeDashboard]);

  const renderDashboard = () => {
    switch (activeDashboard) {
      case "products":
        return <Products />;
      case "clients":
        return <Clients />;
      case "suppliers":
        return <Suppliers />;
      case "reforma-tributaria":
        return <ReformaTributaria />;
      case "validacao-fiscal":
        return <ValidacaoFiscal />;
      case "plcom":
        return <PLCom />;
      case "transactions":
        return <Transactions />;
      case "notificacoes-fiscais":
        return (
          <SubscriptionGate feature="ecac" featureLabel="Notificações Fiscais (eCAC/DEC)">
            <NotificacoesFiscais />
          </SubscriptionGate>
        );
      case "users":
        return <Users />;
      case "projects":
        return <Projects />;
      case "admin":
        return <Admin />;
      case "sped-reviewer":
        return <Revisor />;
      case "downloads":
        return <Downloads />;
      case "sped-processor":
        return <SpedProcessor />;
      case "xml-scan":
        return <XmlScan />;
      case "creditos":
        return (
          <SubscriptionGate feature="creditos" featureLabel="Créditos Tributários">
            <Creditos />
          </SubscriptionGate>
        );
      case "certificates":
        return <Certificates />;
      case "email-marketing":
        return <EmailMarketing />;
      case "nfe-downloader":
        return (
          <SubscriptionGate feature="browserAutomation" featureLabel="Download XMLs via Browser">
            <NfeDownloader />
          </SubscriptionGate>
        );
      case "lead-scraper":
        return <LeadScraper />;
      case "activity-logs":
        return <ActivityLogs />;
      case "automec-fornecedores":
        return isAutomecProject(currentProject) ? <AutomecFornecedores /> : <Files />;
      case "files":
      default:
        return <Files />;
    }
  };


  return (
    <Container $electron={electron}>
      <Menu
        menuMinimized={menuMinimized}
        activeDashboard={activeDashboard}
        onDashboardChange={changeDashboard}
        onMenuToggle={onMenuToggle}
        onOpenCertificateModal={onOpenCertificateModal}
        onOpenXmlDownloadModal={onOpenXmlDownloadModal}
      />
      <Overlay data-tour="display" $menuMinimized={menuMinimized}>
        <div key={activeDashboard} style={{ width: '100%', height: '100%', overflow: 'auto' }}>
          {renderDashboard()}
        </div>
      </Overlay>
    </Container>
  );
}
